import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import { getTimeSeriesDataForEquipment } from "../../services/TimeSeriesService";
import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";

const colors = [0x00b088, 0x003e51, 0xff4438, 0xccdb2a, 0xcccccc, 0x333333];
const LineGraph = (props) => {
	const oneSecond = 1000;
	const oneMinute = 60 * oneSecond;
	const oneHour = 60 * oneMinute;
	const oneDay = 24 * oneHour;
	const oneWeek = 7 * oneDay;
	const oneMonth = 4 * oneWeek;
	const oneYear = 12 * oneMonth;

	const [data, setData] = useState();
	const [labels, setLabels] = useState();
	const [endDate, setEndDate] = useState(new Date());
	const [startDate, setStartDate] = useState(new Date(endDate.getTime() - oneDay));
	const [selectedInterval, setSelectedInterval] = useState();
	const [intervals, setIntervals] = useState({});

	useEffect(() => {
		const delta = endDate.getTime() - startDate.getTime();
		const allowedIntervals = []
		if (delta > oneYear) {
			allowedIntervals.push('24H')
		} else if (delta > oneMonth) {
			allowedIntervals.push('12H','24H')
		} else if (delta > oneWeek) {
			allowedIntervals.push('3H', '6H', '12H','24H')
		} else if (delta > oneHour) {
			allowedIntervals.push('1min', '5min', '15min','30min', '1H')
		} else {
			allowedIntervals.push('1min', '5min', '15min')
		}
		const allIntervals = {
			'1min': '1 Minute',
			'5min': '5 Minutes',
			'15min': '15 Minutes',
			'30min': '30 Minutes',
			'1H': '1 Hour',
			'3H': '3 Hours',
			'6H': '6 Hours',
			'12H': '12 Hours',
			'24H': '1 Day'
		}
		const newIntervals = allowedIntervals.reduce((acc, interval) => {
			acc[interval] = allIntervals[interval]
			return acc
		}, {})
		setIntervals(newIntervals);
	}, [startDate, endDate, oneYear, oneMonth, oneWeek, oneDay, oneHour])

	useEffect(() => {
		if (!intervals.hasOwnProperty(selectedInterval ?? '')) {
			const firstInterval = Object.keys(intervals).sort((a, b) => {
				let ai = 0, bi = 0;
				if (a.indexOf('min') > -1) {
					ai = parseInt(a, 10) * oneMinute
				} else {
					ai = parseInt(a, 10) * oneHour
				}
				if (b.indexOf('min') > -1) {
					bi = parseInt(b, 10) * oneMinute
				} else {
					bi = parseInt(b, 10) * oneHour
				}
				return ai - bi
			})[0]
			setSelectedInterval(firstInterval)
		}
	}, [selectedInterval, intervals, oneMinute, oneHour])

	useEffect(() => {
		if (props.equipment && selectedInterval && startDate && endDate && endDate) {
			setData([]);

			getTimeSeriesDataForEquipment(
				props.equipment.site.site_id,
				props.equipment.equipment_id,
				startDate,
				endDate,
				selectedInterval
			).then((response) => {

				let localData = {};
				let localLabels = {};

				response.forEach((t) => {
					if (localData[t.time]) {
						localData[t.time][t.point_name] = t.value;
					} else {
						localData[t.time] = {};
						localData[t.time][t.point_name] = t.value;
						localData[t.time]["time"] = Date.parse(t.time);
					}

					if (!localLabels[t.point_name] && t.point_name) {
						let nameParts = t.point_name.split("_");
						let name = "";
						nameParts.forEach((p) => {
							let part =
								p.substring(0, 1).toUpperCase() + p.substring(1, p.length);
							name += name.length > 0 ? " " + part : part;
						});

						localLabels[t.point_name] = name;
					}
				});

				if (!localData || localData.length === 0) {
					setData([]);
					return;
				}

				setLabels(localLabels);
				setData(Object.values(localData));
			});
		}
	}, [props.equipment, startDate, endDate, selectedInterval]);

	const handleEventDateChange = (newStartDate, newEndDate) => {
		if (newStartDate !== startDate) {
			setStartDate(newStartDate);
			if (newEndDate.getTime() < newStartDate.getTime()) {
				setEndDate(new Date(Math.min(new Date().getTime(), newStartDate.getTime() + oneDay)))
			}
		} else if (newEndDate !== endDate) {
			setEndDate(newEndDate);
			if (newEndDate.getTime() < newStartDate.getTime()) {
				setStartDate(new Date(newEndDate.getTime() - oneDay))
			}
		}
	};

	useLayoutEffect(() => {
		let root = am5.Root.new("line-graph");

		const myTheme = am5.Theme.new(root);

		myTheme.rule("Label").setAll({
			fontSize: "12px",
			fontFamily: "Lato",
		});

		root.setThemes([am5themes_Animated.new(root), myTheme]);

		let chart = root.container.children.push(
			am5xy.XYChart.new(root, {
				panY: false,
				layout: root.verticalLayout,
			})
		);

		// Create Y-axis
		var yAxis = chart.yAxes.push(
			am5xy.ValueAxis.new(root, {
				renderer: am5xy.AxisRendererY.new(root, {}),
			})
		);

		var xAxis = chart.xAxes.push(
			am5xy.CategoryDateAxis.new(root, {
				baseInterval: { timeUnit: "hour", count: 6 },
				renderer: am5xy.AxisRendererX.new(root, {}),
				categoryField: "time",
				tooltipDateFormat: "MM/dd HH:mm",
			})
		);
		if (data) xAxis.data.setAll(data);
		xAxis.tooltipDateFormat = "HH:mm";

		if (data && data.length) {
			let index = 0;
			Object.keys(data[0]).forEach((o) => {
				if (o !== "time") {
					let series = chart.series.push(
						am5xy.LineSeries.new(root, {
							name: labels[o],
							xAxis: xAxis,
							yAxis: yAxis,
							valueYField: o,
							categoryXField: "time",
							valueXField: "time",
							fill: am5.color(index < colors.length ? colors[index] : 0x000000),
							stroke: am5.color(
								index < colors.length ? colors[index] : 0x000000
							),
						})
					);
					series.data.setAll(data);

					series.strokes.template.setAll({
						strokeWidth: 3,
					});

					if (index == 0) {
						var tooltip = series.set(
							"tooltip",
							am5.Tooltip.new(root, {
								pointerOrientation: "horizontal",
							})
						);

						let text = "[width: 130px]";
						Object.keys(labels).forEach((l) => {
							text += labels[l] + "[/] {" + l + "}\n";
						});

						tooltip.label.setAll({
							text: text,
							fill: 0xffffff,
						});
					}

					index++;
				}
			});
		}

		// Add legend
		let legend = chart.children.push(
			am5.Legend.new(root, {
				width: am5.percent(100),
				centerX: am5.percent(50),
				x: am5.percent(50),
				height: 60,

				layout: am5.GridLayout.new(root, {
					maxColumns: 5,
					fixedWidthGrid: true,
				}),

				verticalScrollbar: am5.Scrollbar.new(root, {
					orientation: "vertical",
				}),
			})
		);

		legend.markers.template.setAll({
			width: 10,
			height: 10,
		});

		legend.data.setAll(chart.series.values);

		xAxis.set("tooltip", am5.Tooltip.new(root, {}));
		yAxis.set("tooltip", am5.Tooltip.new(root, {}));

		// Add cursor
		chart.set("cursor", am5xy.XYCursor.new(root, {}));

		return () => {
			root.dispose();
		};
	}, [data, labels]);

	return (
		<div className="line-graph-container">
			<div className="graph-date-picker">
				<KeyboardDateTimePicker
					autoOk
					disableFuture={true}
					variant="inline"
					inputVariant="outlined"
					label="Start Time"
					format="MM/dd/yyyy hh:mm a"
					size="small"
					value={startDate}
					onChange={(date) => {
						handleEventDateChange(date.toJSDate(), endDate);
					}}
				/>
				<KeyboardDateTimePicker
					autoOk
					disableFuture={true}
					variant="inline"
					inputVariant="outlined"
					label="End Time"
					format="MM/dd/yyyy hh:mm a"
					size="small"
					style={{ marginLeft: 10 }}
					value={endDate}
					onChange={(date) => {
						handleEventDateChange(startDate, date.toJSDate());
					}}
				/>
				<FormControl style={{ marginTop: -10, marginLeft: 10 }}>
  				<InputLabel style={{ position: 'relative', marginBottom: -17, marginTop: 3 }}>Interval</InputLabel>
					{selectedInterval !== undefined && (
						<Select
							native={false}
							value={selectedInterval}
							onChange={(e) => {
								setSelectedInterval(e.target.value);
							}}
						>
							{Object.keys(intervals).map((i) => (
								<MenuItem selected={selectedInterval === i} key={i} value={i}>
									{intervals[i]}
								</MenuItem>
							))}
						</Select>
					)}
				</FormControl>
			</div>
			<div
				id="line-graph"
				className="line-graph"
				style={{ width: "100%", height: "300px" }}
			/>
		</div>
	);
};

export default LineGraph;
